import { ProjectDesignerRoleTags, InfrastructureRequirement } from '@nohara-dxdev/mt-common';
import { PaginationResponse } from '@nohara-dxdev/react-core';
import { JSONSchema7 } from 'json-schema';

import { ItemTemplateEntityWithSchema } from '@/types/api';

/**
 * Inverse of the item-template CSV import parser
 * (`./item-template-csv-parser.ts`). Produces CSV rows whose columns line up
 * with what `createItemTemplateCsvParser` expects, so an exported file
 * re-imports without edits. The round-trip is locked down by the unit test.
 */

// Resolve a setting id (construction type / item type) back to its display
// value name. Falls back to the raw id so an orphaned setting is still visible.
export type ResolveSettingName = (id: string) => string;

export type FetchItemTemplatePage = (
  cursor: string | null | undefined,
) => Promise<PaginationResponse<ItemTemplateEntityWithSchema>>;

// Walk every page of the cursor-paginated templates endpoint so the export
// covers all templates, not just the page the grid happens to have loaded.
// Stops when the API reports no more pages (or returns no next cursor).
export const collectAllItemTemplates = async (
  fetchPage: FetchItemTemplatePage,
): Promise<ItemTemplateEntityWithSchema[]> => {
  const templates: ItemTemplateEntityWithSchema[] = [];
  let cursor: string | null | undefined;

  do {
    const page = await fetchPage(cursor);
    templates.push(...page.data);
    cursor = page.hasMore ? page.cursor : null;
  } while (cursor);

  return templates;
};

const BOOL_TRUE = 'yes';
const BOOL_FALSE = 'no';

// Canonical Japanese role names, matching the JA entries the import parser maps
// from (`./project-role-tags.ts`, sourced from default-app-roles.ts). Export
// emits these so the file stays human-readable and re-imports cleanly.
const roleTagToName: Record<ProjectDesignerRoleTags, string> = {
  [ProjectDesignerRoleTags.LeadArchitectDesigner]: '意匠設計責任者',
  [ProjectDesignerRoleTags.LeadStructureDesigner]: '構造設計責任者',
  [ProjectDesignerRoleTags.LeadElectricalDesigner]: '電気設備設計責任者',
  [ProjectDesignerRoleTags.LeadMachineryFacilityDesigner]: '機械設備設計責任者',
  [ProjectDesignerRoleTags.ArchitectDesigner]: '意匠設計者',
  [ProjectDesignerRoleTags.StructureDesigner]: '構造設計者',
  [ProjectDesignerRoleTags.ElectricalFacilityDesigner]: '電気設備設計者',
  [ProjectDesignerRoleTags.MachineryFacilityDesigner]: '機械設備設計者',
};

export const mapTagToRoleName = (tag: string): string =>
  tag in roleTagToName ? roleTagToName[tag as ProjectDesignerRoleTags] : tag;

export const formatInfrastructureRequirementsForCsv = (
  requirements: InfrastructureRequirement[] = [],
): string =>
  requirements
    .map((req) => {
      const parts: string[] = [req.infrastructureType, req.relationshipLevel];
      if (req.infrastructureRelationship) {
        parts.push(req.infrastructureRelationship);
      }
      return parts.join('|');
    })
    .join(',');

interface AttributeSchema {
  title?: string;
  type?: string;
  enum?: string[];
  'x-unit'?: string;
  'x-order'?: number;
}

const formatAttributeCell = (schema: AttributeSchema): string => {
  const name = schema.title ?? '';
  const unitSuffix = schema['x-unit'] ? `, ${schema['x-unit']}` : '';

  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    return `${name}, Dropdown:${schema.enum.join('|')}${unitSuffix}`;
  }
  if (schema.type === 'number') {
    return `${name}, Number${unitSuffix}`;
  }
  return `${name}, Text${unitSuffix}`;
};

// Attribute columns (index 8+), ordered by the `x-order` the import parser
// stamped on each property so columns come back in their original sequence.
export const formatAttributesForCsv = (jsonSchema?: JSONSchema7): string[] => {
  const properties = jsonSchema?.properties;
  if (!properties) return [];

  return Object.values(properties)
    .filter((prop): prop is JSONSchema7 => typeof prop === 'object')
    .map((prop) => prop as AttributeSchema)
    .sort((a, b) => (a['x-order'] ?? 0) - (b['x-order'] ?? 0))
    .map(formatAttributeCell);
};

export const itemTemplateToCsvRow = (
  template: ItemTemplateEntityWithSchema,
  resolveSettingName: ResolveSettingName,
): string[] => {
  const fixed = [
    template.name,
    template.templateKey,
    template.constructionTypeIds.map(resolveSettingName).join(','),
    template.projectRoleTags.map(mapTagToRoleName).join(','),
    template.isBoardItem ? BOOL_TRUE : BOOL_FALSE,
    template.canConnectToBoard ? BOOL_TRUE : BOOL_FALSE,
    resolveSettingName(template.itemTypeId),
    formatInfrastructureRequirementsForCsv(template.infrastructureRequirements),
  ];

  return [...fixed, ...formatAttributesForCsv(template.jsonSchema)];
};

export interface ItemTemplateCsvExport {
  headers: string[];
  rows: string[][];
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

// Header row is cosmetic for re-import (the parser reads by column index), but
// kept meaningful for humans. Reuses the existing list-column labels; attribute
// columns are labelled by 1-based position.
export const buildItemTemplateCsvExport = (
  templates: ItemTemplateEntityWithSchema[],
  resolveSettingName: ResolveSettingName,
  t?: TranslateFn,
): ItemTemplateCsvExport => {
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) =>
    t ? t(key, options) : fallback;

  const fixedHeaders = [
    tr('itemTemplates.list.columns.name', 'Name'),
    tr('itemTemplates.list.columns.templateKey', 'Template Key'),
    tr('itemTemplates.list.columns.constructionTypes', 'Construction Types'),
    tr('itemTemplates.list.columns.projectRoles', 'Project Roles'),
    tr('itemTemplates.list.columns.isBoardItem', 'Board Item'),
    tr('itemTemplates.list.columns.canConnectToBoard', 'Can Connect to Board'),
    tr('itemTemplates.list.columns.itemType', 'Item Type'),
    tr('itemTemplates.list.columns.infrastructureRequirements', 'Infrastructure Requirements'),
  ];

  const rows = templates.map((template) => itemTemplateToCsvRow(template, resolveSettingName));

  const maxAttributeColumns = rows.reduce(
    (max, row) => Math.max(max, row.length - fixedHeaders.length),
    0,
  );
  const attributeHeaders = Array.from({ length: maxAttributeColumns }, (_, index) =>
    tr('itemTemplates.csvExport.attribute', `Attribute ${index + 1}`, { index: index + 1 }),
  );

  return { headers: [...fixedHeaders, ...attributeHeaders], rows };
};
